import { compareAsc, isAfter, isBefore, isEqual } from 'date-fns';
import { useState } from 'react';

import { useTimeoutFn } from '@sorare/core/src/hooks/animations/useTimeoutFn';
import { groupBy } from '@sorare/core/src/lib/arrays';
import type { LeagueLaunch } from '@sorare/marketplace/src/lib/newSeason';

type Props<T, C> = {
  leagues: LeagueLaunch<T, C>[];
};

const MAX_TIMEOUT_DELAY = 2 ** 31 - 1;

export const useSortLeagueLaunches = <T, C>({ leagues }: Props<T, C>) => {
  const [now, setNow] = useState(() => new Date());

  const nextStartDate = leagues
    .map(league => league.startDate)
    .filter(date => isAfter(date, now))
    .sort(compareAsc)[0];

  const msUntilNextStartDate = nextStartDate
    ? Math.min(nextStartDate.getTime() - now.getTime(), MAX_TIMEOUT_DELAY)
    : null;

  useTimeoutFn(() => setNow(new Date()), msUntilNextStartDate);

  const ascSortedLeagues = [...leagues].sort((a, b) =>
    compareAsc(a.startDate, b.startDate)
  );
  const descSortedLeagues = [...ascSortedLeagues].reverse();

  const mostRecentFromNowLeagueLaunchDate = descSortedLeagues.find(
    league => now > league.startDate && league.canBeFeatured
  )?.startDate;

  const featuredLeagueLaunch = leagues.find(league => league.featured) || null;

  const liveLeagueLaunches = featuredLeagueLaunch
    ? [featuredLeagueLaunch]
    : leagues.filter(
        league =>
          !!mostRecentFromNowLeagueLaunchDate &&
          isEqual(league.startDate, mostRecentFromNowLeagueLaunchDate)
      );

  const releasedLeagueLaunches = ascSortedLeagues
    .filter(league =>
      isBefore(
        league.startDate,
        featuredLeagueLaunch ? now : mostRecentFromNowLeagueLaunchDate || now
      )
    )
    .filter(
      league => !liveLeagueLaunches.map(l => l.slug).includes(league.slug)
    );

  const upcomingLeagueLaunches = ascSortedLeagues.filter(league =>
    isAfter(league.startDate, mostRecentFromNowLeagueLaunchDate || now)
  );

  const liveAndUpcomingLeagueLaunches = [
    ...liveLeagueLaunches,
    ...upcomingLeagueLaunches,
  ];

  const liveAndUpcomingLeagueLaunchesDates = [
    ...new Set(
      liveAndUpcomingLeagueLaunches.reduce((acc, league) => {
        if (!acc.includes(league.startDate.toISOString())) {
          acc.push(league.startDate.toISOString());
        }
        return acc;
      }, [] as string[])
    ),
  ].sort(compareAsc);

  const groupedByDateLiveAndUpcomingLeagueLaunches = groupBy(
    liveAndUpcomingLeagueLaunches,
    l => l.startDate.toISOString()
  );

  return {
    releasedLeagueLaunches,
    upcomingLeagueLaunches,
    liveLeagueLaunches,
    liveAndUpcomingLeagueLaunches,
    liveAndUpcomingLeagueLaunchesDates,
    groupedByDateLiveAndUpcomingLeagueLaunches,
  };
};
